import { useCallback, useEffect, useRef, useState } from "react";
import { QueryStore, type AsyncState } from "@oshimap/query";

export type { AsyncState };

/**
 * 공유 QueryStore 를 웹 화면에 잇는 **얇은 훅**. Native 의 `useQuery` 와 짝이다.
 *
 * 캐시 규칙(중복 요청 합치기 · 무효화 · 취소)은 `@oshimap/query` 한 곳에만 있고,
 * 훅은 플랫폼마다 따로 둔다 — 훅을 공유하면 두 플랫폼이 서로 다른 React
 * 인스턴스를 거쳐 같은 훅을 부르게 되고, 그때 나는 오류는 원인을 찾기 어렵다.
 *
 * staleMs 는 0 이다. 이 제품에서 신선함은 기능이 아니라 제품 자체라서
 * (P4) 화면에 들어올 때마다 다시 받는다. 캐시가 하는 일은 지난 값을 보여
 * 주는 것이 아니라, **같은 키가 동시에 여러 번 요청되는 것을 한 번으로
 * 합치는 것**이다. 지금 실제로 겹치는 것은 React 가 개발 모드에서 효과를
 * 두 번 실행하는 구간과, 탭을 빠르게 오갈 때다.
 */
export const store = new QueryStore({ staleMs: 0 });

export function useQuery<T>(
  keyParts: readonly unknown[],
  fn: (signal: AbortSignal) => Promise<T>,
  opts: { enabled?: boolean } = {},
): { state: AsyncState<T>; reload: () => void } {
  const enabled = opts.enabled ?? true;
  const key = QueryStore.key(keyParts);
  const [state, setState] = useState<AsyncState<T>>(() => store.get<T>(key));
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    if (!enabled) return;
    setState(store.get<T>(key));
    const off = store.subscribe<T>(key, setState);
    void store.fetch<T>(key, (s) => fnRef.current(s));
    return off;
  }, [key, enabled]);

  const reload = useCallback(() => {
    void store.fetch<T>(key, (s) => fnRef.current(s), { force: true });
  }, [key]);

  return { state, reload };
}
